import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Role } from "@/types/domain";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: Role;
}

interface AuthState {
  user: AuthUser | null;
}

const STORAGE_KEY = "luxfair.auth.user";

const DEFAULT_USER: AuthUser = {
  id: "dev-user",
  name: "Dev User",
  email: "dev@born.mt",
  role: "finance_admin",
};

function persist(user: AuthUser | null) {
  if (typeof window === "undefined") return;
  if (user) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  else window.localStorage.removeItem(STORAGE_KEY);
}

function readStored(): AuthUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

const initialState: AuthState = {
  user: readStored() ?? DEFAULT_USER,
};

// Make sure the stored user is written back on first load.
persist(initialState.user);

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    signIn(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      persist(action.payload);
    },
    setRole(state, action: PayloadAction<Role>) {
      const next: AuthUser = state.user
        ? { ...state.user, role: action.payload }
        : { ...DEFAULT_USER, role: action.payload };
      state.user = next;
      persist(next);
    },
    signOut(state) {
      state.user = null;
      persist(null);
    },
  },
});

export const { signIn, setRole, signOut } = slice.actions;
export default slice.reducer;
